/**
 * Training API — modules, attempts, assignments, scenarios, questions CRUD.
 */

import { apiFetch } from './core'

export const training = {
    modules: (params = {}) => {
        const qs = new URLSearchParams(params).toString()
        return apiFetch(`/training/modules${qs ? `?${qs}` : ''}`)
    },
    getModule: (id) => apiFetch(`/training/modules/${id}`),
    createModule: (data) => apiFetch('/training/modules', {
        method: 'POST',
        body: JSON.stringify(data),
    }),
    deleteModule: (id) => apiFetch(`/training/modules/${id}`, { method: 'DELETE' }),
    updateModule: (id, data) => apiFetch(`/training/modules/${id}`, {
        method: 'PATCH', body: JSON.stringify(data),
    }),
    scenarios: (moduleId) => apiFetch(`/training/modules/${moduleId}/scenarios`),
    startAttempt: (data) => apiFetch('/training/attempts', {
        method: 'POST',
        body: JSON.stringify(data),
    }),
    submitAnswer: (attemptId, data) => apiFetch(`/training/attempts/${attemptId}/answer`, {
        method: 'POST',
        body: JSON.stringify(data),
    }),
    /**
     * POST /training/answer — submit one per-question answer outside the
     * attempt-API. Used by ScenarioRunner for manual-review / calculator
     * questions: backend creates TrainingAttempt with
     * grading_status='pending_review' so the answer surfaces in the
     * unified review queue.
     */
    answerQuestion: (data) => apiFetch('/training/answer', {
        method: 'POST',
        body: JSON.stringify(data),
    }),
    progress: (employeeId) => apiFetch(`/training/progress/${employeeId}`),
    /**
     * 7-шаговая карта компетенций менеджера (DialogueTrainer v2 Wave 3 PR-4).
     * Возвращает CompetencyMapResponse: ровно 7 ячеек в каноническом порядке
     * journey (inbound→qualify→aml→quote→order→settlement→outcome).
     * RBAC: self OR senior_online_manager OR HR/admin.
     */
    competencyMap: (employeeId) => apiFetch(`/training/competency-map/${employeeId}`),
    leaderboard: () => apiFetch('/training/leaderboard'),
    listAssignments: (moduleId) => {
        const qs = moduleId ? `?module_id=${moduleId}` : ''
        return apiFetch(`/training/assignments${qs}`)
    },
    createAssignment: (data) => apiFetch('/training/assignments', {
        method: 'POST',
        body: JSON.stringify(data),
    }),
    deleteAssignment: (id) => apiFetch(`/training/assignments/${id}`, { method: 'DELETE' }),
    myAssignments: (employeeId) => apiFetch(`/training/my-assignments/${employeeId}`),
    createScenario: (moduleId, data) => apiFetch(`/training/modules/${moduleId}/scenarios`, {
        method: 'POST', body: JSON.stringify(data),
    }),
    updateScenario: (id, data) => apiFetch(`/training/scenarios/${id}`, {
        method: 'PATCH', body: JSON.stringify(data),
    }),
    deleteScenario: (id) => apiFetch(`/training/scenarios/${id}`, { method: 'DELETE' }),
    createQuestion: (scenarioId, data) => apiFetch(`/training/scenarios/${scenarioId}/questions`, {
        method: 'POST', body: JSON.stringify(data),
    }),
    updateQuestion: (id, data) => apiFetch(`/training/questions/${id}`, {
        method: 'PATCH', body: JSON.stringify(data),
    }),
    deleteQuestion: (id) => apiFetch(`/training/questions/${id}`, { method: 'DELETE' }),
    // ВНИМАНИЕ: dialogue POST-пути НАМЕРЕННО с завершающим `/`. Прод-edge
    // (Railway) на POST-путях БЕЗ слэша роняет CORS-preflight (OPTIONS) в 500
    // без CORS-заголовков → браузер блокирует запрос → «Сервер недоступен,
    // офлайн-режим», тренажёр не стартует. Со слэшем preflight=200, бэкенд
    // канонизирует `/path/`→`/path` без 307 (redirect_slashes=False). Эти роуты
    // в isAiTimeoutRoute (core.js) → нормализация слэша их не трогает.
    // Не убирать слэш. Post-mortem 2026-06-16 (диалог-тренажёр partner_manager).
    /** AI roleplay: один сценарий dialogue в модуле без MCQ → evaluate обновляет прогресс на сервере */
    dialogueStart: (payload) => apiFetch('/training/dialogues/start/', {
        method: 'POST',
        body: JSON.stringify(payload),
    }),
    /**
     * Wave 3 PR-7 — preflight 15-min разминка. POST {employee_id?, scenario_id?, persona_cluster?}
     * → {session_token, expires_at, url, scenario_id, persona_cluster}. Senior/admin/methodologist
     * могут назначать чужому сотруднику; обычный online_manager — только себе.
     */
    dialoguePreflight: (payload) => apiFetch('/training/dialogues/preflight/', {
        method: 'POST',
        body: JSON.stringify(payload || {}),
    }),
    /**
     * Отправить сообщение(я) в диалог.
     * @param {string} sessionId
     * @param {string | string[]} textOrMessages - одна строка (legacy) или массив для burst
     */
    dialogueMessage: (sessionId, textOrMessages) => {
        const body = Array.isArray(textOrMessages)
            ? { messages: textOrMessages }
            : { text: textOrMessages }
        return apiFetch(`/training/dialogues/${sessionId}/message/`, {
            method: 'POST',
            body: JSON.stringify(body),
        })
    },
    dialogueEvaluate: (sessionId) => apiFetch(`/training/dialogues/${sessionId}/evaluate/`, { method: 'POST' }),
    dialogueHint: (sessionId, text) => apiFetch(`/training/dialogues/${sessionId}/hint/`, {
        method: 'POST',
        body: JSON.stringify({ text }),
    }),
    /** Per-turn feedback: 1-3 короткие подсказки + 1-2 strengths по одной реплике менеджера. */
    dialogueTurnFeedback: (sessionId, turnIndex) =>
        apiFetch(`/training/dialogues/${sessionId}/turn-feedback/?turn_index=${turnIndex}`, {
            method: 'POST',
        }),
    /** Spaced repetition queue: модули где status=passed AND next_due_at <= now. */
    progressDue: () => apiFetch('/training/progress/due/me'),
    /** Конфиг сценария (admin / senior_online_manager / methodologist) */
    getDialogueConfig: (scenarioId) => apiFetch(`/training/dialogues/scenarios/${scenarioId}/config`),
    updateDialogueConfig: (scenarioId, data) => apiFetch(`/training/dialogues/scenarios/${scenarioId}/config`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }),
    generateDialogueConfig: (scenarioId) => apiFetch(`/training/dialogues/scenarios/${scenarioId}/generate-ai-config/`, {
        method: 'POST',
    }),
    /** Persona Library — published персон для подключения к сценарию */
    listPersonaLibrary: ({ search = '', tag = '', archetype = '', difficulty = '' } = {}) => {
        const qs = new URLSearchParams({ published_only: 'true', limit: '500' })
        if (search) qs.set('search', search)
        if (tag) qs.set('tag', tag)
        if (archetype) qs.set('archetype', archetype)
        if (difficulty) qs.set('difficulty', difficulty)
        return apiFetch(`/personas/?${qs.toString()}`)
    },
    listSessions: (scenarioId, employeeId) => {
        const qs = new URLSearchParams()
        if (scenarioId) qs.set('scenario_id', scenarioId)
        if (employeeId) qs.set('employee_id', employeeId)
        const s = qs.toString()
        return apiFetch(`/training/dialogues/sessions${s ? `?${s}` : ''}`)
    },
    /** Детали одной сессии диалога (score + ai_feedback + транскрипт) — разбор руководителем */
    sessionDetail: (sessionId) => apiFetch(`/training/dialogues/${sessionId}/detail`),
    /** Сводный разбор тренажёра по сотруднику: темы, зоны роста, повестка итоговой встречи */
    trackDebrief: (employeeId) => apiFetch(`/training/dialogues/track-debrief/${employeeId}`),
    /** Переотправить руководителю сводку по треку (сотрудник закрыл трек до автоуведомления) */
    sendTrackDebrief: (employeeId) =>
        apiFetch(`/training/dialogues/track-debrief/${employeeId}/notify`, { method: 'POST' }),
    /** Список обезличенных реальных диалогов из PS2 для Replay-режима */
    realDialogueSamples: (outcome = '') => {
        const qs = new URLSearchParams({ limit: '20' })
        if (outcome) qs.set('outcome', outcome)
        return apiFetch(`/training/dialogues/real/samples?${qs.toString()}`)
    },
    /** Начать сессию с историей из реального диалога PS2 (Продолжи-режим) */
    realDialogueReplay: (chatId, stopAtTurn = 5, employeeId) => {
        const body = { sample_chat_id: chatId, stop_at_turn: stopAtTurn }
        if (employeeId) body.employee_id = employeeId
        return apiFetch('/training/dialogues/real/replay/', {
            method: 'POST',
            body: JSON.stringify(body),
        })
    },
    getSessionDetail: (sessionId) => apiFetch(`/training/dialogues/${sessionId}/detail`),
    /** Шаблоны сообщений из CRM PS2 для диалог-тренажёра */
    messageTemplates: () => apiFetch('/training/dialogues/message-templates'),
    generateScenariosFromReal: (moduleId, maxPerOutcome = 2) =>
        apiFetch('/training/dialogues/scenarios/generate-from-real/', {
            method: 'POST',
            body: JSON.stringify({ module_id: moduleId, max_per_outcome: maxPerOutcome }),
        }),
    /** Локальный UI уже обновлён; серверный MCQ-прогресс — через /training/answer */
    completeScenario: async () => ({}),

    // ── Training AI Pipeline (admin / methodologist) ──────────
    aiStatus: () => apiFetch('/training/ai/status'),
    aiResetAnalysis: () => apiFetch('/training/ai/reset', { method: 'POST' }),
    aiDiagnose: () => apiFetch('/training/ai/diagnose'),
    aiSeedModules: () => apiFetch('/training/ai/seed-modules', { method: 'POST' }),
    aiAnalyze: (limit = 1000) => apiFetch(`/training/ai/analyze?limit=${limit}`, { method: 'POST' }),
    aiFullPipeline: (limit = 1000) => apiFetch(`/training/ai/full-pipeline?limit=${limit}`, { method: 'POST' }),
    aiPushToScenarios: () => apiFetch('/training/ai/push-to-scenarios', { method: 'POST' }),
    aiSeedLearningPath: () => apiFetch('/training/ai/seed-learning-path', { method: 'POST' }),
    aiTeamStats: () => apiFetch('/training/ai/team-stats'),
    aiCalcQuestions: () => apiFetch('/training/ai/calc-questions'),
    aiGenerateCalcQuestions: (count = 12) => apiFetch(
        `/training/ai/calc-questions/generate?count=${count}`,
        { method: 'POST' },
    ),

    // ── Manual Grading Queue (methodologist / HR / senior / dept_head) ──
    reviewQueue: (params = {}) => {
        const qs = new URLSearchParams(params).toString()
        return apiFetch(`/training/review/queue${qs ? `?${qs}` : ''}`)
    },
    reviewItem: (attemptId) => apiFetch(`/training/review/queue/${attemptId}`),
    reviewVerdict: (attemptId, data) => apiFetch(`/training/review/${attemptId}/verdict`, {
        method: 'POST',
        body: JSON.stringify(data),
    }),
    reviewStats: () => apiFetch('/training/review/stats'),
    dismissAttempt: (attemptId) => apiFetch(`/training/review/${attemptId}/dismiss`, { method: 'POST' }),

    // ── Question attachments upload (methodologist / HR / admin) ──
    uploadQuestionAttachment: async (file, questionId) => {
        const fd = new FormData()
        fd.append('file', file)
        const qid = questionId ? `&entity_id=${questionId}` : ''
        return apiFetch(
            `/files/upload?entity_type=training_question_attachment${qid}`,
            { method: 'POST', body: fd, headers: {} },
        )
    },
}
